import { Edge, Node } from '@xyflow/react';

export interface ValidationError {
	field?: string;
	message: string;
	type: 'error' | 'warning';
}

export interface NodeValidation {
	nodeId: string;
	errors: ValidationError[];
	isValid: boolean;
}

export type ValidationResult = Map<string, ValidationError[]>;

/**
 * Extract variable references from a value
 * Returns array of {nodeId, variable} objects
 */
function extractVariableReferences(
	value: any,
): Array<{ nodeId: string; variable: string }> {
	const references: Array<{ nodeId: string; variable: string }> = [];

	if (typeof value === 'string') {
		// Find all variable references - support both {{varName}} and {{nodeId.variable}}
		const allVariables = /\{\{([^}]+)\}\}/g;
		let match;

		while ((match = allVariables.exec(value)) !== null) {
			const fullVar = match[1];

			if (fullVar.includes('.')) {
				// Format: {{nodeId.variable}}
				const parts = fullVar.split('.');
				references.push({
					nodeId: parts[0],
					variable: parts.slice(1).join('.'),
				});
			} else {
				// Format: {{varName}} - simple variable
				references.push({
					nodeId: fullVar,
					variable: '',
				});
			}
		}
	} else if (typeof value === 'object' && value !== null) {
		// Recursively check object properties
		Object.values(value).forEach((val) => {
			references.push(...extractVariableReferences(val));
		});
	}

	return references;
}

/**
 * Get all upstream node IDs for a given node
 */
function getUpstreamNodeIds(nodeId: string, edges: Edge[]): Set<string> {
	const upstreamNodes = new Set<string>();
	const visited = new Set<string>();

	function traverse(currentId: string) {
		if (visited.has(currentId)) return;
		visited.add(currentId);

		// Find all edges that lead to this node
		const incomingEdges = edges.filter((e) => e.target === currentId);

		incomingEdges.forEach((edge) => {
			upstreamNodes.add(edge.source);
			// Recursively traverse upstream
			traverse(edge.source);
		});
	}

	traverse(nodeId);
	return upstreamNodes;
}

/**
 * Validate required fields for a node based on its step type
 */
function validateRequiredFields(
	node: Node,
	stepTypeSchemas?: Map<string, any>,
): ValidationError[] {
	const errors: ValidationError[] = [];
	const stepType = node.data.stepType || node.type;
	const config = node.data.config || {};

	// Skip validation for start nodes
	if (stepType === 'start' || node.type === 'startNode') {
		return errors;
	}

	// Node-specific validation rules
	switch (stepType) {
		case 'template':
			// Template step requires a template ID
			if (!config.templateId) {
				errors.push({
					field: 'templateId',
					message: 'Email template is required',
					type: 'error',
				});
			}
			break;

		case 'send_email':
			// Send email always requires recipient
			if (!config.to) {
				errors.push({
					field: 'to',
					message: 'Recipient email is required',
					type: 'error',
				});
			}
			// If no template, require subject and body
			if (!config.templateId) {
				if (!config.subject) {
					errors.push({
						field: 'subject',
						message: 'Email subject is required when not using a template',
						type: 'error',
					});
				}
				if (!config.body) {
					errors.push({
						field: 'body',
						message: 'Email body is required when not using a template',
						type: 'error',
					});
				}
			}
			break;

		case 'agent':
			if (!config.agentId) {
				errors.push({
					field: 'agentId',
					message: 'Agent selection is required',
					type: 'error',
				});
			}
			break;

		case 'agent_call':
			if (!config.agentId) {
				errors.push({
					field: 'agentId',
					message: 'Agent selection is required',
					type: 'error',
				});
			}
			if (!config.toNumber) {
				errors.push({
					field: 'toNumber',
					message: 'Phone number is required for agent calls',
					type: 'error',
				});
			}
			// Validate timeout range if enabled
			if (config.maxWaitTimeSeconds > 0) {
				if (config.maxWaitTimeSeconds < 60) {
					errors.push({
						field: 'maxWaitTimeSeconds',
						message: 'Timeout must be at least 60 seconds',
						type: 'error',
					});
				} else if (config.maxWaitTimeSeconds > 3600) {
					errors.push({
						field: 'maxWaitTimeSeconds',
						message: 'Timeout cannot exceed 3600 seconds (1 hour)',
						type: 'error',
					});
				}
			}
			break;

		case 'condition':
			if (!config.conditions || config.conditions.length === 0) {
				errors.push({
					field: 'conditions',
					message: 'At least one condition is required',
					type: 'error',
				});
			}
			break;

		case 'delay':
			// Check for new complex format
			if (!config.delayType) {
				errors.push({
					field: 'delayType',
					message: 'Delay type is required',
					type: 'error',
				});
			} else if (config.delayType === 'relative') {
				// For relative delays, check for value and unit
				if (config.relativeValue == null) {
					errors.push({
						field: 'relativeValue',
						message: 'Delay duration is required',
						type: 'error',
					});
				}
				if (!config.relativeUnit) {
					errors.push({
						field: 'relativeUnit',
						message: 'Delay unit is required',
						type: 'error',
					});
				}
			}
			break;

		case 'wait_for_event':
			if (!config.eventType) {
				errors.push({
					field: 'eventType',
					message: 'Event type is required',
					type: 'error',
				});
			}
			if (!config.constraint) {
				errors.push({
					field: 'constraint',
					message: 'Event constraint is required',
					type: 'error',
				});
			}
			break;

		case 'action':
			if (!config.actionType) {
				errors.push({
					field: 'actionType',
					message: 'Action type is required',
					type: 'error',
				});
			}
			break;

		case 'split':
			if (config.splitRatio == null) {
				errors.push({
					field: 'splitRatio',
					message: 'Split ratio is required',
					type: 'error',
				});
			}
			break;

		case 'load_data':
			if (!config.dataType) {
				errors.push({
					field: 'dataType',
					message: 'Data type is required',
					type: 'error',
				});
			}
			if (!config.entityId) {
				errors.push({
					field: 'entityId',
					message: 'Entity ID is required',
					type: 'error',
				});
			}
			break;
	}

	// Check if node has any configuration at all (warning for unconfigured nodes)
	if (!node.data.isConfigured && Object.keys(config).length === 0) {
		errors.push({
			message: 'Node has not been configured',
			type: 'warning',
		});
	}

	return errors;
}

/**
 * Validate variable references in a node's configuration
 */
function validateVariableReferences(
	node: Node,
	nodes: Node[],
	edges: Edge[],
	stepTypeSchemas?: Map<string, any>,
): ValidationError[] {
	const errors: ValidationError[] = [];
	const config = node.data.config || {};

	// Extract all variable references from the config
	const references = extractVariableReferences(config);

	// Get all upstream nodes for this node
	const upstreamNodeIds = getUpstreamNodeIds(node.id, edges);

	// Check if there's a bodyVariableMapping (for manual email body mode)
	const bodyVariableMapping = config.bodyVariableMapping || {};

	references.forEach((ref) => {
		// Check if this is a user-defined variable (simple {{varName}} format)
		// These are custom variables defined in bodyVariableMapping
		if (bodyVariableMapping[ref.nodeId] && !ref.variable) {
			// This is a custom body variable - it's valid because it will be transformed to the actual reference
			return;
		}

		// Check if this is a mapped variable with step.field format
		const fullVariableName = `${ref.nodeId}.${ref.variable}`;
		if (bodyVariableMapping[fullVariableName]) {
			// This is a mapped variable - it's valid because it will be transformed to the actual reference
			return;
		}

		// Special handling for system variables
		if (ref.nodeId === 'System' || ref.nodeId === 'system') {
			// System variables are always valid
			return;
		}

		// Special handling for Start variables
		if (ref.nodeId === 'Start') {
			// Check if Start node exists
			const startNode = nodes.find(
				(n) => n.type === 'startNode' || n.data.stepType === 'start',
			);
			if (!startNode) {
				errors.push({
					message: `Referenced Start node not found`,
					type: 'error',
				});
			}
			// TODO: Could validate specific Start variables based on config
			return;
		}

		// Special handling for system variables
		if (ref.nodeId === 'system') {
			// System variables are always valid - they're provided by the system at runtime
			// Examples: system.currentDate, system.currentTime, etc.
			return;
		}

		// Check if referenced node exists
		const referencedNode = nodes.find((n) => n.id === ref.nodeId);
		if (!referencedNode) {
			errors.push({
				message: `Referenced step "${ref.nodeId}" not found (variable: ${ref.variable})`,
				type: 'error',
			});
			return;
		}

		// Check if referenced node is upstream
		if (!upstreamNodeIds.has(ref.nodeId)) {
			errors.push({
				message: `Referenced step "${referencedNode.data.label}" is not connected upstream (variable: ${ref.variable})`,
				type: 'error',
			});
			return;
		}

		// Check if variable exists in the referenced node's output schema
		if (stepTypeSchemas) {
			const referencedStepType =
				referencedNode.data.stepType || referencedNode.type;
			const outputSchema = stepTypeSchemas.get(referencedStepType);

			if (outputSchema && !outputSchema[ref.variable]) {
				errors.push({
					message: `Variable "${ref.variable}" not found in step "${referencedNode.data.label}" outputs`,
					type: 'warning',
				});
			}
		}
	});

	return errors;
}

/**
 * Validate an entire flow
 */
export function validateFlow(
	nodes: Node[],
	edges: Edge[],
	stepTypeSchemas?: Map<string, any>,
): ValidationResult {
	const validationResult: ValidationResult = new Map();

	nodes.forEach((node) => {
		const errors: ValidationError[] = [];

		// Validate required fields
		errors.push(...validateRequiredFields(node, stepTypeSchemas));

		// Validate variable references
		errors.push(
			...validateVariableReferences(node, nodes, edges, stepTypeSchemas),
		);

		// Only add to result if there are errors
		if (errors.length > 0) {
			validationResult.set(node.id, errors);
		}
	});

	return validationResult;
}

/**
 * Check if a flow is valid (has no errors, warnings are OK)
 */
export function isFlowValid(validationResult: ValidationResult): boolean {
	for (const errors of validationResult.values()) {
		if (errors.some((e) => e.type === 'error')) {
			return false;
		}
	}
	return true;
}

/**
 * Get a summary of validation errors
 */
export function getValidationSummary(validationResult: ValidationResult): {
	errorCount: number;
	warningCount: number;
	nodeCount: number;
} {
	let errorCount = 0;
	let warningCount = 0;

	validationResult.forEach((errors) => {
		errors.forEach((error) => {
			if (error.type === 'error') {
				errorCount++;
			} else {
				warningCount++;
			}
		});
	});

	return {
		errorCount,
		warningCount,
		nodeCount: validationResult.size,
	};
}
